import React, { useState, ChangeEvent, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import ConfigureNotification from '@/assets/img/configure-notification.svg';
import UserData from '@/assets/img/image-user-data.svg';
import NotifyImage from '@/assets/img/notify-img.svg';
import PasswordIllustration from '@/assets/img/set-a-password.svg';
import SetPhone from '@/assets/img/set-a-phone.svg';
import CardInfos from '@/components/CardInfos';
import {
  DeactivateAccountModal,
  IDeleteRequests,
} from '@/components/Modals/DeactivateAccountModal';
import { LogoutModal } from '@/components/Modals/LogoutModal';
import ModalDefault from '@/components/Modals/ModalDefault';
import ModalResetUsersInfo, {
  CurrentResetUsersInfoModal,
} from '@/components/Modals/ModalResetUsersInfo';
import StepByStepCard from '@/components/StepByStepCard';
import PendingApproval from '@/components/UserGestation/PendingApproval';
import { useUser } from '@/contexts/AuthContext';
import { useLoading } from '@/contexts/LoadingContext';
import MainContainer from '@/layouts/MainContainer';
import { Toast } from '@/lib/Toast';
import { api } from '@/services/apiClient';
import { User } from '@/types/api/users';
import { hasParamsConfigureNotifications, priorityUserName } from '@/utils';
import {
  ALLOWED_IMAGE_FILE_TYPES,
  MAXIMUM_IMG_SIZE_ALLOWED,
  ROUTER_PAGES,
} from '@/utils/constants';
import { handleErrorAsync } from '@/utils/handlers';
import { cpfMask, phoneMask } from '@/utils/masks';
import { Facebook, Instagram, LinkedIn } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import {
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import { parseCookies, setCookie } from 'nookies';

interface IMyAccountModal {
  isOpen: boolean;
  onClose: () => void;
}

enum CurrentComponent {
  HOME,
  USER_DATA,
}

const DELETE_REQUESTS_INFO_COOKIES = 'dont_show_active_delete_request_info';

const MyAccountModal = ({ isOpen, onClose }: IMyAccountModal) => {
  const router = useRouter();
  const { configure_notifications } = router.query;

  const cookies = parseCookies();
  const { user, updateUserData } = useUser();
  const avatarInput = useRef<HTMLInputElement | null>(null);
  const { setIsLoading } = useLoading();

  const [currentComponent, setCurrentComponent] = useState<CurrentComponent>(
    CurrentComponent.HOME
  );
  const [currentResetUsersInfoModal, setCurrentResetUsersInfoModal] =
    useState<CurrentResetUsersInfoModal>(
      hasParamsConfigureNotifications(configure_notifications)
        ? CurrentResetUsersInfoModal.RESET_CONFIGURE_NOTIFICATION
        : CurrentResetUsersInfoModal.CLOSED
    );
  const [isOpenModalDeactivateAccount, setIsOpenModalDeactivateAccount] =
    useState(false);
  const [isOpenModalLogout, setIsOpenModalLogout] = useState(false);
  const [deleteRequests, setDeleteRequests] = useState<IDeleteRequests[]>([]);
  const [dontShowActiveDeleteRequestInfo, setDontShowActiveDeleteRequestInfo] =
    useState(!!cookies[DELETE_REQUESTS_INFO_COOKIES]);
  const [openModalShouldInviteAdmin, setOpenModalShouldInviteAdmin] =
    useState(false);

  const hasActiveDeleteRequest = deleteRequests.find(
    ({ is_pending }) => is_pending
  );
  const daysRemainingToDeactivateAccount = getDaysRemainingToDeactivateAccount(
    hasActiveDeleteRequest?.created_at || ''
  );

  const returnToModalHome = () => setCurrentComponent(CurrentComponent.HOME);

  const handleChangeAvatarImg = async (e: ChangeEvent<HTMLInputElement>) => {
    const [response, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      const fileFormData = new FormData();
      const avatarImgFile = e.target.files;

      if (!avatarImgFile || avatarImgFile.length === 0) {
        return;
      }

      if (avatarImgFile[0].size >= MAXIMUM_IMG_SIZE_ALLOWED) {
        throw Error('Tamanho máximo de arquivo excedido (30 mb)');
      }

      if (!ALLOWED_IMAGE_FILE_TYPES.includes(avatarImgFile[0].type)) {
        Toast(
          'error',
          'Formato não suportado! (São permitidos: PNG, JPG, JPEG)'
        );
        return;
      }

      fileFormData.append('avatar', avatarImgFile[0]);
      fileFormData.append('name', user?.name || '');
      fileFormData.append('cpf', user?.cpf || '');
      fileFormData.append('email', user?.email || '');
      fileFormData.append('username', user?.username || '');

      return await api.put<User>('/auth/users/me/', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    });

    if (error) {
      setIsLoading(false);
      Toast('error', error.message);
      if (avatarInput.current) {
        avatarInput.current.value = '';
      }
      return;
    }

    updateUserData(response?.data || {});
    setIsLoading(false);
  };

  const handleClose = () => {
    onClose();
  };

  const cancelDeleteAccountRequests = async () => {
    const [_, error] = await handleErrorAsync(async () => {
      setIsLoading(true);
      await api.post('auth/users/cancel_delete_account/');
      setDeleteRequests(prevState =>
        prevState.map(deleteRequest =>
          deleteRequest.id === hasActiveDeleteRequest?.id
            ? {
                ...deleteRequest,
                is_pending: false,
              }
            : deleteRequest
        )
      );
      Toast('success', 'Solicitação de deleção cancelada com sucesso!');
    }).finally(() => {
      setIsLoading(false);
    });

    if (error) return Toast('error', error.message);
  };

  function getDaysRemainingToDeactivateAccount(createdAt: string) {
    const dateIn30DaysAfter = new Date(createdAt);
    dateIn30DaysAfter.setDate(dateIn30DaysAfter.getDate() + 31);

    return Math.floor(
      (dateIn30DaysAfter.getTime() - new Date().getTime()) /
        (1000 * 60 * 60 * 24)
    );
  }

  const setCookiesToExpireTomorrow = () => {
    const tomorrowDate = new Date(
      `2022-10-${String(new Date().getDate() + 1).padStart(
        2,
        '0'
      )}T00:00:00.000Z`
    );
    setCookie(undefined, DELETE_REQUESTS_INFO_COOKIES, String(true), {
      expires: tomorrowDate,
    });
  };

  const redirectInviteAdmin = () => {
    setOpenModalShouldInviteAdmin(false);
    router
      .push({
        pathname: ROUTER_PAGES.home,
        query: { invite_admin: true },
      })
      .finally(() => {
        returnToModalHome();
        onClose();
      });
  };

  useEffect(() => {
    const getDeleteRequests = async () => {
      const [data, error] = await handleErrorAsync(async () => {
        setIsLoading(true);
        const { data } = await api.get<IDeleteRequests[]>(
          'auth/users/delete_account/'
        );
        return data;
      }).finally(() => {
        setIsLoading(false);
      });

      if (error) return Toast('error', error.message);

      setDeleteRequests(data);
    };
    if (isOpen && !dontShowActiveDeleteRequestInfo) getDeleteRequests();
  }, [setIsLoading, dontShowActiveDeleteRequestInfo, isOpen]);

  return (
    <Modal
      aria-labelledby="modal-my-account"
      aria-describedby="modal-my-account-description"
      data-testid="modal-my-account"
      sx={{ overflowY: 'auto' }}
      open={isOpen}
      onClose={handleClose}
    >
      <>
        <Box
          sx={{
            boxShadow: '0px 1px 1px 0px #0000001A',
            background: '#FFFFFF',
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '18px 16px',
            position: 'relative',
            zIndex: 999,
          }}
        >
          <IconButton
            onClick={() => {
              if (currentComponent === CurrentComponent.HOME) {
                handleClose();
                return;
              }
              returnToModalHome();
            }}
            sx={{ color: '#000000', position: 'absolute', left: '16px' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h2">
            Minha conta
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: 'background.default' }}>
          <MainContainer
            sx={{
              paddingY: hasActiveDeleteRequest ? '16px' : '24px',
              display: 'flex',
              gap: '16px',
              flexDirection: 'column',
              minHeight: 'calc(100vh - 68px)',
            }}
          >
            {hasActiveDeleteRequest && !dontShowActiveDeleteRequestInfo && (
              <Box
                component="section"
                sx={{
                  backgroundColor: 'warning.light',
                  color: 'warning.dark',
                  borderRadius: '8px',
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '20px',
                  position: 'relative',
                }}
              >
                <InfoOutlinedIcon
                  sx={theme => ({
                    fontSize: '51px',
                    display: 'none',
                    [theme.breakpoints.up('sm')]: {
                      display: 'flex',
                      textAlign: 'right',
                    },
                  })}
                />
                <Box sx={{ flex: '1', paddingRight: 'calc(40px - 16px)' }}>
                  <IconButton
                    onClick={() => {
                      setDontShowActiveDeleteRequestInfo(true);
                      setCookiesToExpireTomorrow();
                    }}
                    sx={{
                      position: 'absolute',
                      top: 1,
                      right: 1,
                    }}
                  >
                    <CloseIcon
                      sx={{ color: 'text.primary', right: '8px', top: '8px' }}
                    />
                  </IconButton>
                  <Typography variant="h6" component="h4">
                    Faltam {daysRemainingToDeactivateAccount}{' '}
                    {daysRemainingToDeactivateAccount === 1 ? 'dia' : 'dias'}{' '}
                    para a desativação!
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={theme => ({
                      display: 'none',
                      mb: '4px',
                      [theme.breakpoints.up('sm')]: {
                        display: 'block',
                      },
                    })}
                  >
                    A sua solicitação de desativação da conta esta com status
                    pendente!
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={theme => ({
                      textAlign: 'left',
                      display: 'block',
                      [theme.breakpoints.up('sm')]: {
                        textAlign: 'right',
                      },
                    })}
                  >
                    Cancele a desativação
                    <Button
                      onClick={cancelDeleteAccountRequests}
                      sx={{
                        textDecoration: 'underline',
                        textTransform: 'none',
                        color: 'inherit',
                        p: 0,
                        ml: '2px',
                      }}
                    >
                      <Typography variant="subtitle2" component="strong">
                        clicando aqui
                      </Typography>
                    </Button>
                  </Typography>
                </Box>
              </Box>
            )}

            <PendingApproval />

            <Typography component="h3" variant="h6">
              {
                {
                  [CurrentComponent.HOME]: 'Informações pessoais',
                  [CurrentComponent.USER_DATA]: 'Editar dados pessoais',
                }[currentComponent]
              }
            </Typography>

            {
              {
                [CurrentComponent.HOME]: (
                  <>
                    <Box sx={{ marginBottom: '24px', textAlign: 'center' }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        badgeContent={
                          <IconButton
                            onClick={() => avatarInput.current?.click()}
                          >
                            <Avatar
                              sx={{
                                backgroundColor: '#FFF',
                                color: 'rgba(0,0,0,0.5)',
                                border: '1px solid',
                              }}
                            >
                              <CameraAltOutlinedIcon />
                            </Avatar>
                          </IconButton>
                        }
                      >
                        <Avatar
                          alt="Avatar do usuário"
                          src={user?.avatar || '/'}
                          sx={{
                            height: '135px',
                            width: '135px',
                            border: '1px solid #C7C7C7',
                            backgroundColor: '#E9E9E9',
                          }}
                        >
                          {!user?.avatar && (
                            <PersonOutlineIcon
                              sx={{ fontSize: '48px', color: '#C7C7C7' }}
                            />
                          )}
                        </Avatar>
                      </Badge>
                      <input
                        className="sr-only"
                        ref={avatarInput}
                        type="file"
                        data-testid="avatar-img-input"
                        onChange={handleChangeAvatarImg}
                        accept={'image/png, image/jpg, image/jpeg'}
                      />
                      <Typography variant="h6" sx={{ marginTop: '24px' }}>
                        {priorityUserName(user)}
                      </Typography>
                      <Box display="flex" justifyContent="center">
                        {user?.facebook_url && (
                          <Link href={user?.facebook_url} passHref>
                            <a
                              target="_blank"
                              rel="noreferrer"
                              data-testid="FacebookIconLink"
                            >
                              <IconButton>
                                <Facebook sx={{ color: '#000000' }} />
                              </IconButton>
                            </a>
                          </Link>
                        )}
                        {user?.instagram_url && (
                          <Link href={user?.instagram_url} passHref>
                            <a
                              target="_blank"
                              rel="noreferrer"
                              data-testid="InstagramIconLink"
                            >
                              <IconButton>
                                <Instagram sx={{ color: '#000000' }} />
                              </IconButton>
                            </a>
                          </Link>
                        )}
                        {user?.linkedin_url && (
                          <Link href={user?.linkedin_url} passHref>
                            <a
                              target="_blank"
                              rel="noreferrer"
                              data-testid="LinkedinIconLink"
                            >
                              <IconButton>
                                <LinkedIn sx={{ color: '#000000' }} />
                              </IconButton>
                            </a>
                          </Link>
                        )}
                      </Box>
                    </Box>
                    <CardInfos
                      rows={[
                        { title: 'Apelido', content: user?.display_name },
                        { title: 'Nome completo', content: user?.name },
                        { title: 'E-mail', content: user?.email },
                        {
                          title: 'Celular',
                          content: phoneMask(user?.phone || ''),
                        },
                        { title: 'CPF', content: cpfMask(user?.cpf || '') },
                      ]}
                      onClick={() =>
                        setCurrentComponent(CurrentComponent.USER_DATA)
                      }
                      buttonIdentification="button-to-user-data-modal"
                    />
                    <CardInfos
                      rows={[
                        {
                          title: 'Facebook',
                          content: user?.facebook_url && (
                            <CheckCircleIcon sx={{ color: '#019E07' }} />
                          ),
                        },
                        {
                          title: 'Instagram',
                          content: user?.instagram_url && (
                            <CheckCircleIcon sx={{ color: '#019E07' }} />
                          ),
                        },
                        {
                          title: 'Linkedin',
                          content: user?.linkedin_url && (
                            <CheckCircleIcon sx={{ color: '#019E07' }} />
                          ),
                        },
                      ]}
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_SOCIAL_MEDIA
                        )
                      }
                      buttonIdentification="button-to-social-media-modal"
                    />
                    <CardInfos
                      rows={[{ title: 'Senha', content: '***********' }]}
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_PASSWORD
                        )
                      }
                      buttonIdentification="button-to-reset-password-modal"
                    />
                    <Box
                      component="button"
                      onClick={() => setIsOpenModalLogout(true)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '42px',
                        padding: '0 16px 16px',
                        opacity: '50%',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'none',
                      }}
                    >
                      <ExitToAppIcon />
                      <Typography variant="body2">Sair</Typography>
                    </Box>
                    <Box
                      component="button"
                      onClick={() => setIsOpenModalDeactivateAccount(true)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '42px',
                        padding: '0 16px 16px',
                        opacity: '50%',
                        cursor: 'pointer',
                        border: 'none',
                        background: 'none',
                      }}
                    >
                      <DeleteForeverIcon />
                      <Typography variant="body2">Desativar conta</Typography>
                    </Box>
                  </>
                ),
                [CurrentComponent.USER_DATA]: (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fill, minmax(280px, 1fr))',
                      gap: '0 25px',
                    }}
                  >
                    <StepByStepCard
                      imageProps={{
                        src: UserData,
                        alt: 'Ilustração do card de alterar o nome',
                      }}
                      title="Alterar meu nome"
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_NAME
                        )
                      }
                      subtitle={user?.name && `${user?.name}`}
                    />
                    <StepByStepCard
                      imageProps={{
                        src: PasswordIllustration,
                        alt: 'Ilustração do card de alterar o e-mail',
                      }}
                      title="Alterar meu e-mail"
                      subtitle={user?.email}
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_EMAIL
                        )
                      }
                    />
                    <StepByStepCard
                      imageProps={{
                        src: PasswordIllustration,
                        alt: 'Ilustração do card de cadastrar um e-mail alternativo',
                      }}
                      title="E-mail alternativo"
                      subtitle={user?.alternative_email}
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_ALTERNATIVE_EMAIL
                        )
                      }
                    />
                    <StepByStepCard
                      imageProps={{
                        src: SetPhone,
                        alt: 'Ilustração do card de alterar o email',
                      }}
                      title="Alterar meu telefone"
                      subtitle={phoneMask(user?.phone || '')}
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_PHONE
                        )
                      }
                    />
                    <StepByStepCard
                      imageProps={{
                        src: UserData,
                        alt: 'Ilustração do card de alterar o apelido',
                      }}
                      title="Alterar meu apelido"
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_DISPLAY_NAME
                        )
                      }
                      subtitle={user?.display_name && `${user?.display_name}`}
                    />
                    <StepByStepCard
                      imageProps={{
                        src: ConfigureNotification,
                        alt: 'Ilustração do card de configurar notificação',
                      }}
                      title="Configurar notificações"
                      onClick={() =>
                        setCurrentResetUsersInfoModal(
                          CurrentResetUsersInfoModal.RESET_CONFIGURE_NOTIFICATION
                        )
                      }
                      subtitle="Configure o recebimento de notificações"
                    />
                  </Box>
                ),
              }[currentComponent]
            }
          </MainContainer>
        </Box>

        <ModalResetUsersInfo
          currentModal={currentResetUsersInfoModal}
          returnToHome={() => {
            returnToModalHome();
            setCurrentResetUsersInfoModal(CurrentResetUsersInfoModal.CLOSED);
          }}
          handleClose={() => {
            setCurrentComponent(CurrentComponent.USER_DATA);
            setCurrentResetUsersInfoModal(CurrentResetUsersInfoModal.CLOSED);
          }}
          closeModalMyAccount={onClose}
        />

        <DeactivateAccountModal
          isOpen={isOpenModalDeactivateAccount}
          onClose={() => setIsOpenModalDeactivateAccount(false)}
          onSuccess={newDeleteRequest => {
            setDeleteRequests([newDeleteRequest]);
          }}
        />
        <LogoutModal
          isOpen={isOpenModalLogout}
          onClose={() => {
            setIsOpenModalLogout(false), handleClose();
          }}
        />

        <ModalDefault
          isOpen={openModalShouldInviteAdmin}
          onClose={() => setOpenModalShouldInviteAdmin(false)}
          src={NotifyImage}
          alt="Imagem ilustrando um aviso"
          title="Atenção fornecedor!"
          subtitle="Esta empresa ainda não possui um administrador. Defina um administrador ou envie um convite por e-mail."
          textButton="Definir administrador"
          onClickButton={redirectInviteAdmin}
        />
      </>
    </Modal>
  );
};

export type { IMyAccountModal };
export { DELETE_REQUESTS_INFO_COOKIES };

export default MyAccountModal;
